/**
 * Top-level Report Builder — wires together the field tree, chart-type
 * picker, axis design, multi-Y axes, filters, layout panel, and live
 * preview into a single 12-column wizard layout.
 *
 * Loads the indicator on mount, lets the user mutate `config` locally,
 * and persists changes via PATCH `/reporting/indicators/{id}/`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import {
  reportingAPI,
  type DashboardIndicator,
  type IndicatorBuilderConfig,
} from '../../../lib/api/reporting';
import { useDsidCatalog } from './useDsidCatalog';
import { useOperatorCatalog } from './useOperatorCatalog';
import { useIndicatorPreview } from './useIndicatorPreview';
import { FieldPickerTree } from './FieldPickerTree';
import { ChartTypePicker, type BuilderChartType } from './ChartTypePicker';
import { AxisDesignPanel } from './AxisDesignPanel';
import { ConditionalFormatPanel } from './ConditionalFormatPanel';
import { GridSettingsPanel } from './GridSettingsPanel';
import { MultiYAxisPanel } from './MultiYAxisPanel';
import { FilterEditor } from './FilterEditor';
import { LayoutPanel } from './LayoutPanel';
import { PreviewPanel } from './PreviewPanel';
import { SaveBar } from './SaveBar';

interface ReportBuilderProps {
  indicatorId: number;
  /** Called when the user clicks Cancel or successfully saves. */
  onClose?: () => void;
}

const ALLOWED_CHART_TYPES: ReadonlyArray<BuilderChartType> = [
  'grid', 'bar', 'pie', 'donut', 'line', 'kpi',
  'indicator', 'summary', 'heatmap', 'waterfall', 'geolocation', 'markup',
];

function asChartType(raw: string | undefined): BuilderChartType {
  if (raw && (ALLOWED_CHART_TYPES as ReadonlyArray<string>).includes(raw)) {
    return raw as BuilderChartType;
  }
  return 'bar';
}

function asConfig(raw: Record<string, unknown> | undefined): IndicatorBuilderConfig {
  if (!raw) return {};
  return raw as IndicatorBuilderConfig;
}

export default function ReportBuilder({ indicatorId, onClose }: ReportBuilderProps) {
  const { catalog, loading: catalogLoading } = useDsidCatalog();
  const { catalog: operatorCatalog, loading: operatorLoading } = useOperatorCatalog();

  const [indicator, setIndicator] = useState<DashboardIndicator | null>(null);
  const [config, setConfig] = useState<IndicatorBuilderConfig>({});
  const [chartType, setChartType] = useState<BuilderChartType>('bar');
  const [title, setTitle] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [dirty, setDirty] = useState<boolean>(false);

  // Load the indicator on mount / when id changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reportingAPI
      .getIndicator(indicatorId)
      .then((res) => {
        if (cancelled) return;
        const ind = res.data;
        setIndicator(ind);
        setConfig(asConfig(ind.config));
        setChartType(asChartType(ind.chart_type));
        setTitle(ind.name);
        setDirty(false);
      })
      .catch(() => {
        if (cancelled) return;
        toast.error('Failed to load indicator');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [indicatorId]);

  const updateConfig = useCallback((next: IndicatorBuilderConfig) => {
    setConfig(next);
    setDirty(true);
  }, []);

  const updateTitle = useCallback((next: string) => {
    setTitle(next);
    setDirty(true);
  }, []);

  const updateChartType = useCallback((next: BuilderChartType) => {
    setChartType(next);
    setDirty(true);
  }, []);

  const previewState = useIndicatorPreview(indicatorId, config, chartType);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const description = typeof config.description === 'string' ? config.description : '';
      const payload: Partial<DashboardIndicator> = {
        name: title.trim() || (indicator?.name ?? 'Untitled'),
        chart_type: chartType,
        // The Layout panel writes the description into config.description for
        // preview convenience, but the model stores it as `hint` and the
        // IndicatorCard reads `hint` — copy it across so the saved card
        // actually shows the description the user just typed.
        hint: description.trim(),
        config: config as unknown as Record<string, unknown>,
      };
      const res = await reportingAPI.updateIndicator(indicatorId, payload);
      setIndicator(res.data);
      setDirty(false);
      toast.success('Indicator saved');
    } catch (err) {
      console.error('Save failed:', err);
      const message = err instanceof Error ? err.message : 'Failed to save indicator';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }, [indicatorId, indicator, chartType, config, title]);

  const handleCancel = useCallback(() => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    onClose?.();
  }, [dirty, onClose]);

  const showMultiY = useMemo(
    () => chartType === 'bar' || chartType === 'line',
    [chartType],
  );

  const showGridSettings = useMemo(
    () => chartType === 'grid',
    [chartType],
  );

  if (loading || catalogLoading || operatorLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onClose && (
            <Button variant="ghost" size="sm" onClick={handleCancel} className="h-8">
              <ArrowLeft className="h-3.5 w-3.5 mr-1" />
              Back
            </Button>
          )}
          <div>
            <h1 className="text-xl font-semibold">Report Builder</h1>
            <p className="text-xs text-muted-foreground">
              Configure axes, filters, and layout. Changes preview live on the right.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* LEFT — Field tree */}
        <Card className="col-span-12 lg:col-span-3">
          <CardContent className="p-0 h-[calc(100vh-220px)] flex flex-col">
            <FieldPickerTree
              catalog={catalog}
              loading={catalogLoading}
              search={search}
              onSearchChange={setSearch}
            />
          </CardContent>
        </Card>

        {/* MIDDLE — Designer */}
        <div className="col-span-12 lg:col-span-6 space-y-3">
          <Card>
            <CardContent className="pt-4 space-y-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Chart Type
              </div>
              <ChartTypePicker value={chartType} onChange={updateChartType} />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <AxisDesignPanel
                config={config}
                onChange={updateConfig}
                catalog={catalog}
                chartType={chartType}
              />
            </CardContent>
          </Card>
          {showMultiY && (
            <Card>
              <CardContent className="pt-4">
                <MultiYAxisPanel
                  config={config}
                  onChange={updateConfig}
                  catalog={catalog}
                />
              </CardContent>
            </Card>
          )}
          {showGridSettings && (
            <Card>
              <CardContent className="pt-4">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                  Grid Settings
                </div>
                <GridSettingsPanel
                  config={config}
                  onChange={updateConfig}
                  catalog={catalog}
                />
              </CardContent>
            </Card>
          )}
          <Card>
            <CardContent className="pt-4">
              <FilterEditor
                config={config}
                onChange={updateConfig}
                catalog={catalog}
                operatorCatalog={operatorCatalog}
              />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <LayoutPanel
                config={config}
                onChange={updateConfig}
                chartType={chartType}
                title={title}
                onTitleChange={updateTitle}
              />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <ConditionalFormatPanel
                config={config}
                onChange={updateConfig}
                catalog={catalog}
              />
            </CardContent>
          </Card>
        </div>

        {/* RIGHT — Live preview */}
        <div className="col-span-12 lg:col-span-3">
          <PreviewPanel
            preview={previewState.preview}
            loading={previewState.loading}
            chartType={chartType}
          />
        </div>
      </div>

      <SaveBar
        saving={saving}
        previewLoading={previewState.loading}
        onSave={handleSave}
        onCancel={handleCancel}
        onPreview={previewState.refresh}
        message={dirty ? 'Unsaved changes' : 'Saved'}
      />
    </div>
  );
}
